/**
 * A única porta do cockpit para a API.
 *
 * Três decisões moram aqui, e as três vêm do ADR-009:
 *
 * **O token de acesso vive em memória, nunca em `localStorage`.** Quem sobrevive ao
 * recarregar a página é o cookie de refresh, que é `HttpOnly` — inalcançável por script,
 * inclusive por script injetado. Guardar o acesso em `localStorage` entregaria de graça o
 * que o `HttpOnly` protege, em troca de não chamar `/auth/renovar` uma vez na partida.
 *
 * **Todo 401 tenta renovar uma vez e repete a requisição.** O acesso é curto de propósito;
 * sem isto, o operador seria expulso da tela no meio de uma validação a cada poucos
 * minutos. A repetição é *uma* — se a renovação falhar, a sessão acabou de verdade.
 *
 * **Escrita sempre manda `Authorization`.** O cookie de acesso autoriza só GET/HEAD/OPTIONS
 * (é o que permite `<img src>` funcionar), então um POST sem cabeçalho não é "quase certo":
 * é 401 garantido. Este módulo nunca depende do cookie para escrever.
 */

/** Códigos que a tela trata de forma diferente. Cada um é uma frase distinta para o usuário. */
export type MotivoDeRecusa =
  | "modulo_nao_licenciado"
  | "cota_esgotada"
  // O login: a senha conferiu e falta o código do aplicativo — ou falta cadastrá-lo (T-12).
  | "segundo_fator"
  | "cadastrar_segundo_fator"
  | "segundo_fator_indisponivel"
  // O limite por usuário das rotas caras (T-12), distinto do 429 do login.
  | "limite_por_usuario"
  // A validade do acesso da organização passou (A-09, ADR-060): a frase diz até quando.
  | "acesso_vencido"
  | "desconhecido";

const MOTIVOS_CONHECIDOS: readonly MotivoDeRecusa[] = [
  "modulo_nao_licenciado",
  "cota_esgotada",
  "segundo_fator",
  "cadastrar_segundo_fator",
  "segundo_fator_indisponivel",
  "limite_por_usuario",
  "acesso_vencido",
];

export class ErroDaApi extends Error {
  readonly status: number;
  readonly motivo: MotivoDeRecusa;
  /** O `detail` como veio, para quem precisa de mais que a frase (o cadastro do 2FA). */
  readonly detalhe: unknown;

  constructor(
    mensagem: string,
    status: number,
    motivo: MotivoDeRecusa = "desconhecido",
    detalhe: unknown = undefined,
  ) {
    super(mensagem);
    this.name = "ErroDaApi";
    this.status = status;
    this.motivo = motivo;
    this.detalhe = detalhe;
  }

  /** 402 é contrato, e diz duas coisas diferentes (ADR-012). A tela precisa separar. */
  get semLicenca(): boolean {
    return this.status === 402 && this.motivo === "modulo_nao_licenciado";
  }

  get cotaEsgotada(): boolean {
    return this.status === 402 && this.motivo === "cota_esgotada";
  }

  get sessaoExpirada(): boolean {
    return this.status === 401;
  }
}

/** O que a API devolve no corpo de erro. `detail` pode ser string ou objeto. */
interface CorpoDeErro {
  detail?:
    string | { motivo?: string; mensagem?: string; [k: string]: unknown };
}

let acesso: string | null = null;
let aoPerderSessao: (() => void) | null = null;
/** Quem é avisado a cada renovação que deu certo, com a identidade que veio nela. */
let aoRenovarSessao: ((quem: unknown) => void) | null = null;
/**
 * A frase da última renovação recusada por um motivo que a pessoa precisa ler — hoje, só a
 * validade do acesso (A-09). As outras recusas dizem "Sua sessão terminou", e esta diz até
 * quando a organização entrava e com quem falar.
 */
let fraseDaUltimaRecusa: string | null = null;

export function ultimaRecusaDaSessao(): string | null {
  return fraseDaUltimaRecusa;
}
/** Minutos sem uso até sair sozinho (T-12). Vem da API no login e em cada renovação. */
let inatividadeMinutos = 0;

export function minutosDeInatividade(): number {
  return inatividadeMinutos;
}

export function guardarInatividade(minutos: number | undefined): void {
  inatividadeMinutos = minutos ?? 0;
}

export function guardarAcesso(token: string | null): void {
  acesso = token;
}

export function temAcesso(): boolean {
  return acesso !== null;
}

/** Quem é avisado quando a renovação falha — a aplicação volta para a tela de entrada. */
export function aoExpirar(callback: (() => void) | null): void {
  aoPerderSessao = callback;
}

/**
 * Quem é avisado quando a renovação dá certo, com o `quem` que veio nela: a validade e a marca
 * d'água (A-09) mudam pelo comando do dono do banco, e a tela de quem está dentro acompanha em
 * até 15 minutos, sem recarregar.
 */
export function aoRenovar(callback: ((quem: unknown) => void) | null): void {
  aoRenovarSessao = callback;
}

function lerMotivo(corpo: CorpoDeErro): MotivoDeRecusa {
  const detalhe = corpo.detail;
  if (
    detalhe &&
    typeof detalhe === "object" &&
    typeof detalhe.motivo === "string"
  ) {
    const motivo = MOTIVOS_CONHECIDOS.find((m) => m === detalhe.motivo);
    if (motivo) return motivo;
  }
  return "desconhecido";
}

function lerMensagem(corpo: CorpoDeErro, reserva: string): string {
  const detalhe = corpo.detail;
  if (typeof detalhe === "string") return detalhe;
  if (
    detalhe &&
    typeof detalhe === "object" &&
    typeof detalhe.mensagem === "string"
  ) {
    return detalhe.mensagem;
  }
  return reserva;
}

/**
 * As frases de quando **não há** corpo do servidor para ler: o proxy (o nginx) responde 502/504
 * com HTML, e o `statusText` do navegador é inglês ("Bad Gateway") ou vazio em HTTP/2. A pessoa
 * lê português, e a frase diz o que fazer (revisão geral de 30/10, `N249`).
 */
const FRASE_DO_STATUS: Record<number, string> = {
  502: "O servidor não respondeu agora. Tente de novo em instantes.",
  503: "O servidor está ocupado agora. Tente de novo em instantes.",
  504: "O servidor demorou demais para responder. Tente de novo.",
};

/** A rede caiu (ou o servidor nem foi achado): não há resposta, e o `fetch` lança. */
export const SEM_CONEXAO =
  "Sem conexão com o servidor. Confira a internet e tente de novo.";

let renovacaoEmCurso: Promise<boolean> | null = null;

/**
 * Renova o acesso usando o cookie de refresh. Devolve se conseguiu.
 *
 * Sem `Authorization` de propósito: a credencial desta chamada é o cookie, e mandar um
 * token vencido junto só daria à API um motivo a mais para recusar.
 *
 * **Uma renovação por vez, e quem chegar no meio espera a que já está em curso.** Isto não
 * é otimização: o refresh é de **uso único com rotação** — cada uso queima o token e emite
 * outro. Duas chamadas concorrentes saem com o mesmo token, a segunda chega depois de ele
 * já ter sido queimado, recebe 401, e a aplicação conclui que a sessão acabou. O usuário é
 * mandado para o login com um cookie perfeitamente válido no navegador.
 *
 * Acontece em dois lugares reais, e nenhum deles é hipotético: o `StrictMode` do React roda
 * o efeito de partida duas vezes em desenvolvimento, e em produção basta a tela disparar
 * duas requisições em paralelo com o acesso vencido — que é o caso normal de uma tela que
 * carrega fila e telemetria ao mesmo tempo.
 */
export function renovar(): Promise<boolean> {
  renovacaoEmCurso ??= executarRenovacao();
  return renovacaoEmCurso;
}

async function executarRenovacao(): Promise<boolean> {
  try {
    const resposta = await fetch("/auth/renovar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    if (!resposta.ok) {
      const corpo = (await resposta.json().catch(() => ({}))) as CorpoDeErro;
      fraseDaUltimaRecusa =
        lerMotivo(corpo) === "acesso_vencido" ? lerMensagem(corpo, "") : null;
      return false;
    }
    const sessao = (await resposta.json()) as {
      acesso?: string;
      inatividade_minutos?: number;
      quem?: unknown;
    };
    if (!sessao.acesso) return false;
    acesso = sessao.acesso;
    fraseDaUltimaRecusa = null;
    guardarInatividade(sessao.inatividade_minutos);
    if (sessao.quem) aoRenovarSessao?.(sessao.quem);
    return true;
  } catch {
    // Rede fora. Não é sessão expirada — é a API inalcançável, e quem diz isso na tela é a
    // pílula de estado do sistema, não um redirecionamento para o login.
    return false;
  } finally {
    // Liberado só no fim: enquanto a chamada está no ar, quem pedir renovação recebe esta
    // mesma promessa. Depois dela, uma renovação nova é legítima — o token já é outro.
    renovacaoEmCurso = null;
  }
}

interface Opcoes extends Omit<RequestInit, "body"> {
  corpo?: unknown;
  /** Uso interno: marca a segunda tentativa, para não renovar em laço. */
  jaRenovou?: boolean;
  /**
   * Para a chamada que **cria** a sessão (`POST /auth/sessao`). O 401 dela é e-mail ou senha
   * errados, não sessão vencida: renovar não faz sentido, e trocar a frase do servidor por
   * "Sua sessão expirou" mandou gente de volta ao login achando que o sistema caiu (25/09).
   */
  semRenovar?: boolean;
  /** A resposta é texto (um CSV gerado pela API), e não JSON. */
  texto?: boolean;
}

/**
 * Faz a chamada, renova uma vez em 401, e transforma erro em `ErroDaApi`.
 *
 * `T` é o formato esperado do corpo. Os tipos de verdade vêm de `esquema.ts`, gerado do
 * OpenAPI da API — este módulo não os conhece e não deve conhecer.
 */
export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const { corpo, jaRenovou, semRenovar, texto, headers, ...resto } = opcoes;

  const cabecalhos = new Headers(headers);
  if (acesso) cabecalhos.set("Authorization", `Bearer ${acesso}`);

  let payload: BodyInit | undefined;
  if (corpo instanceof FormData) {
    // Sem `Content-Type`: quem escreve o boundary é o navegador, e defini-lo à mão quebra o
    // upload de um jeito que só aparece no servidor.
    payload = corpo;
  } else if (corpo !== undefined) {
    cabecalhos.set("Content-Type", "application/json");
    payload = JSON.stringify(corpo);
  }

  let resposta: Response;
  try {
    resposta = await fetch(caminho, {
      ...resto,
      headers: cabecalhos,
      ...(payload !== undefined ? { body: payload } : {}),
    });
  } catch (falha) {
    // Quem cancelou o pedido (um `AbortController`) sabe que cancelou: não é erro de rede.
    if (falha instanceof DOMException && falha.name === "AbortError")
      throw falha;
    throw new ErroDaApi(SEM_CONEXAO, 0);
  }

  if (resposta.status === 401 && !jaRenovou && !semRenovar) {
    if (await renovar()) {
      return api<T>(caminho, { ...opcoes, jaRenovou: true });
    }
    acesso = null;
    aoPerderSessao?.();
    throw new ErroDaApi("Sua sessão expirou. Entre de novo.", 401);
  }

  if (!resposta.ok) {
    const corpoDoErro = (await resposta
      .json()
      .catch(() => ({}))) as CorpoDeErro;
    throw new ErroDaApi(
      lerMensagem(
        corpoDoErro,
        FRASE_DO_STATUS[resposta.status] ??
          `Não foi possível concluir (erro ${resposta.status}).`,
      ),
      resposta.status,
      lerMotivo(corpoDoErro),
      corpoDoErro.detail,
    );
  }

  if (resposta.status === 204) return null as T;
  if (texto) return (await resposta.text()) as T;
  return (await resposta.json()) as T;
}

/**
 * Envio de arquivos **com progresso** — o upload do lote (Gisele, 21/09: "tem que aparecer
 * carregando o lote, um percentual que vai aumentando até 100").
 *
 * `fetch` não diz quanto do corpo já subiu; o `XMLHttpRequest` diz, pelo `upload.onprogress`.
 * As mesmas regras do `api()` valem aqui: `Authorization` sempre (é escrita), uma renovação
 * em 401 e uma repetição, e erro vira `ErroDaApi` com a frase do servidor.
 *
 * `aoProgredir` recebe a fração de 0 a 1 **dos bytes enviados**. Chegar a 1 não é terminar:
 * o servidor ainda confere cada arquivo, e é a promessa que diz quando acabou.
 */
export function enviarComProgresso<T>(
  caminho: string,
  corpo: FormData,
  aoProgredir: (fracao: number) => void,
  jaRenovou = false,
): Promise<T> {
  return new Promise<T>((resolver, recusar) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", caminho);
    if (acesso) xhr.setRequestHeader("Authorization", `Bearer ${acesso}`);
    xhr.upload.onprogress = (evento) => {
      if (evento.lengthComputable && evento.total > 0) {
        aoProgredir(Math.min(1, evento.loaded / evento.total));
      }
    };
    xhr.onerror = () =>
      recusar(
        new ErroDaApi("A conexão caiu durante o envio. Tente de novo.", 0),
      );
    xhr.onload = () => {
      void (async () => {
        if (xhr.status === 401 && !jaRenovou) {
          if (await renovar()) {
            aoProgredir(0);
            resolver(
              await enviarComProgresso<T>(caminho, corpo, aoProgredir, true),
            );
            return;
          }
          acesso = null;
          aoPerderSessao?.();
          recusar(new ErroDaApi("Sua sessão expirou. Entre de novo.", 401));
          return;
        }
        let resposta: unknown = {};
        try {
          resposta = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch {
          resposta = {};
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          const corpoDoErro = resposta as CorpoDeErro;
          recusar(
            new ErroDaApi(
              lerMensagem(corpoDoErro, xhr.statusText || `HTTP ${xhr.status}`),
              xhr.status,
              lerMotivo(corpoDoErro),
            ),
          );
          return;
        }
        aoProgredir(1);
        resolver(resposta as T);
      })().catch(recusar);
    };
    xhr.send(corpo);
  });
}
